import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { fetchData } from '../helper/ApiCall';
import { END_POINT, METHODS } from '../helper/helper';

const OTPVerification = ({ 
    userId, 
    userType, 
    onVerificationSuccess, 
    onCancel,
    userEmail 
}) => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [timeLeft, setTimeLeft] = useState(600); // 10 minutes

    // Countdown timer
    useEffect(() => {
        if (timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [timeLeft]);

    // Format time for display
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Handle OTP input change
    const handleOtpChange = (index, value) => {
        if (value.length > 1) return; // Prevent multiple characters
        
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            if (nextInput) nextInput.focus();
        }
    };

    // Handle backspace
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    // Verify OTP
    const handleVerifyOTP = async () => {
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            Swal.fire({
                title: 'Invalid OTP',
                text: 'Please enter the complete 6-digit OTP',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        setLoading(true);
        try {
            const result = await fetchData(END_POINT.VerifyOTP, METHODS.POST, JSON.stringify({
                userId,
                userType,
                otp: otpString
            }));

            if (result.success) {
                Swal.fire({
                    title: 'Success!',
                    text: 'OTP verified successfully',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
                onVerificationSuccess(result.data);
            } else {
                Swal.fire({
                    title: 'Verification Failed',
                    text: result.message,
                    icon: 'error',
                    confirmButtonText: 'Try Again'
                });
            }
        } catch (err) {
            console.error('OTP verification error:', err);
            Swal.fire({
                title: 'Error',
                text: 'Something went wrong. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    // Resend OTP
    const handleResendOTP = async () => {
        setResendLoading(true);
        try {
            const result = await fetchData(END_POINT.ResendOTP, METHODS.POST, JSON.stringify({
                userId,
                userType
            }));

            if (result.success) {
                setTimeLeft(600); // Reset timer
                setOtp(['', '', '', '', '', '']); // Clear current OTP
                Swal.fire({
                    title: 'OTP Sent',
                    text: 'A new OTP has been sent to your email',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({
                    title: 'Failed to Resend',
                    text: result.message,
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        } catch (err) {
            console.error('Resend OTP error:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to resend OTP. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="otp-verification-container">
            <div className="otp-card">
                <div className="text-center mb-4">
                    <i className="fa-solid fa-shield-alt fa-3x text-primary mb-3"></i>
                    <h3>Security Verification</h3>
                    <p className="text-muted">
                        We've sent a 6-digit verification code to<br />
                        <strong>{userEmail}</strong>
                    </p>
                </div>

                <div className="otp-input-container mb-4">
                    {otp.map((digit, index) => (
                        <input
                            key={index}
                            id={`otp-${index}`}
                            type="text"
                            maxLength="1"
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className="otp-input"
                            autoComplete="off"
                        />
                    ))}
                </div>

                <div className="text-center mb-4">
                    <div className="timer-display">
                        <i className="fa-solid fa-clock me-2"></i>
                        Time remaining: <strong>{formatTime(timeLeft)}</strong>
                    </div>
                </div>

                <div className="d-grid gap-2 mb-3">
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={handleVerifyOTP}
                        disabled={loading || timeLeft === 0}
                    >
                        {loading ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                Verifying...
                            </>
                        ) : (
                            'Verify OTP'
                        )}
                    </Button>
                </div>

                <div className="text-center">
                    <p className="mb-2">Didn't receive the code?</p>
                    <button
                        className="btn btn-link p-0"
                        onClick={handleResendOTP}
                        disabled={resendLoading || timeLeft > 570} // Allow resend after 30 seconds
                    >
                        {resendLoading ? 'Sending...' : 'Resend OTP'}
                    </button>
                </div>

                <div className="text-center mt-4">
                    <button className="btn btn-secondary" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </div>

            <style jsx>{`
                .otp-verification-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    padding: 20px;
                    width: 1500px;
                }

                .otp-card {
                    background: white;
                    border-radius: 15px;
                    padding: 40px;
                    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
                    max-width: 400px;
                    width: 100%;
                }

                .otp-input-container {
                    display: flex;
                    gap: 10px;
                    justify-content: center;
                }

                .otp-input {
                    width: 50px;
                    height: 50px;
                    border: 2px solid #ddd;
                    border-radius: 8px;
                    text-align: center;
                    font-size: 18px;
                    font-weight: bold;
                    transition: border-color 0.3s;
                }

                .otp-input:focus {
                    outline: none;
                    border-color: #007bff;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                }

                .timer-display {
                    color: #666;
                    font-size: 14px;
                }

                @media (max-width: 576px) {
                    .otp-card {
                        padding: 20px;
                    }
                    
                    .otp-input {
                        width: 40px;
                        height: 40px;
                        font-size: 16px;
                    }
                    
                    .otp-input-container {
                        gap: 8px;
                    }
                }
            `}</style>
        </div>
    );
};

export default OTPVerification;
